/*
 * File: components/timesheet/TimesheetQuery.js
 * Mô tả: Giao diện tra cứu và chỉnh sửa dữ liệu chấm công.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiGetTimesheetForEmployee, apiUpdateTimesheetEntry, apiGetEmployeeInfo,apiExportTimesheetForEmployee } from '../../api';

const roundUpToHour = (timeStr, baseDate) => {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date(baseDate);
    date.setHours(hours, minutes, 0, 0);
    if (minutes > 0) {
        date.setHours(date.getHours() + 1, 0, 0, 0);
    }
    return date;
};

const calculateNightAllowance_0_3 = (checkInStr, workDate, isNightShift, standardHours) => {
    if (!isNightShift || !checkInStr || standardHours <= 0) return 0;
    const baseDate = new Date(workDate);
    baseDate.setHours(0, 0, 0, 0);
    const officialStartTime = roundUpToHour(checkInStr, baseDate);
    if (!officialStartTime) return 0;
    const officialWorkEnd = new Date(officialStartTime.getTime() + standardHours * 60 * 60 * 1000);
    const nightWindowStart = new Date(baseDate);
    nightWindowStart.setHours(22, 0, 0, 0);
    const nightWindowEnd = new Date(baseDate);
    nightWindowEnd.setDate(nightWindowEnd.getDate() + 1);
    nightWindowEnd.setHours(6, 0, 0, 0);
    const overlapStart = Math.max(officialStartTime, nightWindowStart);
    const overlapEnd = Math.min(officialWorkEnd, nightWindowEnd);
    const diffMs = overlapEnd - overlapStart;
    if (diffMs > 0) {
        return parseFloat((diffMs / (1000 * 60 * 60)).toFixed(2));
    }
    return 0;
};

const jiaTypeMap = { 
    'A': 'Việc riêng', 'B': 'Phép bệnh', 'E': 'Phép năm', 'C': 'Nghỉ kết hôn', 
    'D': 'Phép tang', 'F': 'Nghỉ thai sản', 'G': 'Nghỉ công tác', 'H': 'Nghỉ C.Thường',
    'I': 'Đi đường', 'K': 'Không lương' 
};

export default function TimesheetQuery() {
    const [searchParamsFromUrl] = useSearchParams();

    const getCurrentYearMonth = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        return `${year}-${month}`;
    };

    const [searchParams, setSearchParams] = useState(() => {
        const empid = searchParamsFromUrl.get('empid');
        const yymm = searchParamsFromUrl.get('yymm');
        return {
            empid: empid || '',
            yymm: yymm || getCurrentYearMonth()
        };
    });

    const [employeeInfo, setEmployeeInfo] = useState(null);
    const [results, setResults] = useState([]);
    const [originalResults, setOriginalResults] = useState([]);
    const [editedRows, setEditedRows] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    
    const SPECIAL_ALLOWANCE_GROUPS = ['A059', 'A073', 'A067'];

    const calculateWorkHoursFrontend = (row) => {
        const { timeup, timedown, workdat, Kzhour } = row;
        const checkInStr = String(timeup || '').replace(':', '');
        const checkOutStr = String(timedown || '').replace(':', '');

        if (!checkInStr || !checkOutStr || !workdat || checkInStr.length < 4 || checkOutStr.length < 4) {
            return { TOTH: row.TOTH || 0, H1: row.H1 || 0, H2: row.H2 || 0, H3: 0, B3: row.B3 || 0 };
        }

        const year = parseInt(workdat.substring(0, 4), 10);
        const month = parseInt(workdat.substring(4, 6), 10) - 1;
        const day = parseInt(workdat.substring(6, 8), 10);

        let checkInDate = new Date(year, month, day, parseInt(checkInStr.substring(0, 2), 10), parseInt(checkInStr.substring(2, 4), 10));
        let checkOutDate = new Date(year, month, day, parseInt(checkOutStr.substring(0, 2), 10), parseInt(checkOutStr.substring(2, 4), 10));

        if (checkOutDate <= checkInDate) { checkOutDate.setDate(checkOutDate.getDate() + 1); }

        let totalMinutes = (checkOutDate - checkInDate) / (1000 * 60);
        totalMinutes -= 60; 
        if (totalMinutes < 0) totalMinutes = 0;

        const roundedMinutes = Math.floor(totalMinutes / 30) * 30;
        const TOTH = parseFloat((roundedMinutes / 60).toFixed(1));

        const standardHours = 8;
        const leaveHours = parseFloat(Kzhour) || 0;
        const effectiveStandardHours = standardHours - leaveHours;

        let H1 = 0, H2 = 0, H3 = 0, B3 = 0, overtimeHours = 0;

        if (TOTH > effectiveStandardHours) {
            overtimeHours = parseFloat((TOTH - effectiveStandardHours).toFixed(1));
        }

        const workDate = new Date(year, month, day);
        const dayOfWeek = workDate.getDay();

        if (overtimeHours > 0) {
            if (dayOfWeek === 0) H2 = overtimeHours; 
            else H1 = overtimeHours; 
        }

        let nightMinutes = 0;
        for (let time = new Date(checkInDate); time < checkOutDate; time.setMinutes(time.getMinutes() + 1)) {
            const hour = time.getHours();
            if (hour >= 22 || hour < 6) nightMinutes++;
        }
        B3 = parseFloat((nightMinutes / 60).toFixed(1));

        return { TOTH, H1, H2, H3, B3 };
    };


    const formatTimeForDisplay = (timeStr) => {
        const timeValue = String(timeStr || '');
        if (!timeValue) return '';
        if (timeValue.length >= 4) {
            return `${timeValue.substring(0, 2)}:${timeValue.substring(2, 4)}`;
        }
        return '';
    };

    const executeSearch = useCallback(async (empIdToSearch, yearMonthToSearch) => {
        if (!empIdToSearch || !yearMonthToSearch) {
            if (empIdToSearch || yearMonthToSearch) {
                setMessage({ type: 'error', text: 'Vui lòng nhập Mã NV và Tháng/Năm.' });
            }
            return;
        }
        setIsLoading(true);
        setMessage({ type: '', text: '' });
        setResults([]);
        setOriginalResults([]);
        setEditedRows({});
        setEmployeeInfo(null);

        try {
            const info = await apiGetEmployeeInfo(empIdToSearch);
            setEmployeeInfo(info);
            const data = await apiGetTimesheetForEmployee(empIdToSearch, yearMonthToSearch.replace('-', ''));
            
            const formattedData = data.map(row => ({
                ...row,
                timeup: formatTimeForDisplay(row.timeup),
                timedown: formatTimeForDisplay(row.timedown)
            }));

            if (data.length === 0) {
                setMessage({ type: 'info', text: 'Không tìm thấy dữ liệu chấm công.' });
            }
            setResults(formattedData);
            setOriginalResults(JSON.parse(JSON.stringify(formattedData))); 
        } catch (err) {
            setEmployeeInfo(null);
            setMessage({ type: 'error', text: err.message });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        const empidFromUrl = searchParamsFromUrl.get('empid');
        const yymmFromUrl = searchParamsFromUrl.get('yymm');
        if (empidFromUrl && yymmFromUrl) {
            executeSearch(empidFromUrl, yymmFromUrl);
        }
    }, [searchParamsFromUrl, executeSearch]);


    const handleParamChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({ ...prev, [name]: value.toUpperCase() }));
        if (name === 'empid') {
            setEmployeeInfo(null);
        }
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        executeSearch(searchParams.empid, searchParams.yymm);
    };

    const handleInputChange = (autoid, field, value) => {
        let newResults = results.map(row => {
            if (row.autoid !== autoid) return row;

            let processedValue;
            if (field === 'timeup' || field === 'timedown') {
                processedValue = value.replace(/[^0-9:]/g, '');
            } else {
                const parsed = value === '-' || value === '' ? 0 : parseFloat(value);
                processedValue = isNaN(parsed) ? row[field] : parsed;
            }
            
            const updatedRow = { ...row, [field]: processedValue };

            if (field === 'Kzhour') {
                const workHours = calculateWorkHoursFrontend(updatedRow);
                const standardHours = 8 - (parseFloat(updatedRow.Kzhour) || 0);
                const workDate = new Date(updatedRow.workdat.substring(0, 4), parseInt(updatedRow.workdat.substring(4, 6), 10) - 1, updatedRow.workdat.substring(6, 8));
                const isNightShift = updatedRow.timeup && updatedRow.timedown && updatedRow.timeup > updatedRow.timedown;
                let phuCap_0_5 = 0;
                if (employeeInfo && SPECIAL_ALLOWANCE_GROUPS.includes(employeeInfo.GROUPID) && standardHours >= 8) {
                    phuCap_0_5 = 0.5;
                }
                const phuCap_0_3 = calculateNightAllowance_0_3(updatedRow.timeup, workDate, isNightShift, standardHours);
                return { ...updatedRow, ...workHours, B4: phuCap_0_5, B5: phuCap_0_3 };
            }
            
            return updatedRow;
        });

        setResults(newResults);
        setEditedRows(prev => ({ ...prev, [autoid]: true }));
    };

    const handleTimeBlur = (autoid, field, value) => {
        const numericValue = value.replace(/[^0-9]/g, '');
        let formattedForDisplay = '';

        if (numericValue.length > 0) {
            const padded = (numericValue + '0000').substring(0, 4);
            formattedForDisplay = `${padded.substring(0, 2)}:${padded.substring(2, 4)}`;
        }
        
        const newResults = results.map(row => {
            if (row.autoid !== autoid) return row;
            
            let newRow = { ...row, [field]: formattedForDisplay };
            
            const workHours = calculateWorkHoursFrontend(newRow);
            
            const standardHours = 8 - (parseFloat(newRow.Kzhour) || 0);
            const workDate = new Date(newRow.workdat.substring(0, 4), parseInt(newRow.workdat.substring(4, 6), 10) - 1, newRow.workdat.substring(6, 8));
            const isNightShift = newRow.timeup && newRow.timedown && newRow.timeup > newRow.timedown;
            let phuCap_0_5 = 0;
            if (employeeInfo && SPECIAL_ALLOWANCE_GROUPS.includes(employeeInfo.GROUPID) && standardHours >= 8) {
                phuCap_0_5 = 0.5;
            }
            const phuCap_0_3 = calculateNightAllowance_0_3(newRow.timeup, workDate, isNightShift, standardHours);
            
            return { ...newRow, ...workHours, B4: phuCap_0_5, B5: phuCap_0_3 };
        });

        setResults(newResults);
    };


    const handleSaveAll = async () => {
        const changedData = results
            .filter(row => editedRows[row.autoid])
            .map(row => {
                const timeup = (String(row.timeup || '').replace(':', '') + '00').padEnd(6, '0');
                const timedown = (String(row.timedown || '').replace(':', '') + '00').padEnd(6, '0');
                return { ...row, timeup, timedown };
            });

        if (changedData.length === 0) return;

        setIsLoading(true);
        setMessage({ type: 'info', text: `Đang lưu ${changedData.length} thay đổi...` });

        try {
            await Promise.all(changedData.map(row => apiUpdateTimesheetEntry(row.autoid, row)));
            setMessage({ type: 'success', text: 'Tất cả thay đổi đã được lưu thành công!' });
            const reFormattedResults = results.map(r => ({...r, timeup: formatTimeForDisplay(r.timeup), timedown: formatTimeForDisplay(r.timedown)}));
            setOriginalResults(JSON.parse(JSON.stringify(reFormattedResults)));
            setEditedRows({});
        } catch (err) {
            setMessage({ type: 'error', text: `Lỗi khi lưu thay đổi: ${err.message}` });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelChanges = () => {
        setResults(originalResults);
        setEditedRows({});
        setMessage({ type: '', text: '' });
    };

    // CẬP NHẬT: Thêm biến limit16h
    const handleExportExcel = async (limit16h = false) => {
        if (!searchParams.empid || !searchParams.yymm) {
            setMessage({ type: 'error', text: 'Thiếu thông tin Mã NV hoặc Tháng để xuất file.' });
            return;
        }
        setIsExporting(true);
        setMessage({ type: 'info', text: 'Đang tạo file Excel, vui lòng đợi...' });
        try {
            await apiExportTimesheetForEmployee(searchParams.empid, searchParams.yymm, limit16h);
            setMessage({ type: 'success', text: `Xuất file ${limit16h ? '16h' : 'gốc'} thành công!` });
        } catch (error) {
            setMessage({ type: 'error', text: `Lỗi khi xuất file: ${error.message}` });
        } finally {
            setIsExporting(false);
        }
    };

    const formatInputValue = (value) => {
        if (value === 0) return '-';
        if (value === null || value === undefined) return '';
        return value;
    };

    return (
        <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Tra cứu & Chỉnh sửa Chấm công</h2>
            <form onSubmit={handleFormSubmit} className="bg-gray-50 p-4 rounded-lg flex items-end gap-4 mb-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Mã Nhân viên</label>
                    <input type="text" name="empid" value={searchParams.empid} onChange={handleParamChange} className="mt-1 p-2 border rounded-md w-40"/>
                </div>
                {employeeInfo && <div className="self-center pb-2 text-blue-600 font-semibold">{employeeInfo.EMPNAM_VN}</div>}
                <div>
                    <label className="block text-sm font-medium text-gray-700">Tháng (YYYY-MM)</label>
                    <input type="month" name="yymm" value={searchParams.yymm} onChange={handleParamChange} className="mt-1 p-2 border rounded-md"/>
                </div>
                <button type="submit" disabled={isLoading} className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                    {isLoading ? 'Đang tìm...' : 'Tìm kiếm'}
                </button>
            </form>

            {message.text && (
                <div className={`p-3 rounded-md text-sm ${
                    message.type === 'error' ? 'bg-red-100 text-red-800' : 
                    message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                }`}>
                    {message.text}
                </div>
            )}

            {results.length > 0 && (
                 <div className="mt-6">
                    <div className="flex justify-between items-end mb-2">
                        <span className="text-gray-600 font-medium">Chi tiết công tháng {searchParams.yymm}</span>
                        <div className="flex gap-2">
                            {/* CẬP NHẬT: 2 Nút xuất */}
                            <button onClick={() => handleExportExcel(false)} disabled={isExporting} className="px-4 py-2 font-semibold text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:bg-gray-400 flex items-center gap-2">
                                {isExporting ? 'Đang xuất...' : '📥 Xuất Excel (Gốc)'}
                            </button>
                            <button onClick={() => handleExportExcel(true)} disabled={isExporting} className="px-4 py-2 font-semibold text-white bg-indigo-500 rounded-md hover:bg-indigo-600 disabled:bg-gray-400 flex items-center gap-2">
                                {isExporting ? 'Đang xuất...' : '📥 Xuất Excel (16h)'}
                            </button>
                        </div>
                    </div>
                    <div className="overflow-auto bg-white rounded-lg shadow" style={{ maxHeight: '70vh' }}>
                        <table className="min-w-full text-sm">
                            <thead className="sticky top-0 bg-gray-100 z-10">
                                <tr>
                                    {['Ngày', 'Giờ vào', 'Giờ ra', 'Giờ vắng', 'Tổng giờ', 'TC 1.5', 'TC 2.0', 'TC 3.0', 'TC Đêm', 'PC 0.5', 'PC 0.3', 'Ghi chú'].map(header => (
                                        <th key={header} className="p-2 text-left font-semibold text-gray-600 whitespace-nowrap border-b">
                                            {header}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {results.map(row => {
                                    const isHoliday = row.JiaType && row.JiaType.trim() !== '';
                                    const jiaTypeCode = row.JiaType ? row.JiaType.trim() : '';

                                    const rowClasses = [
                                        'hover:bg-gray-50',
                                        editedRows[row.autoid] ? 'bg-yellow-50' : '',
                                        isHoliday ? 'bg-sky-200' : ''
                                    ].join(' ');

                                    return (
                                        <tr key={row.autoid} className={rowClasses}>
                                            <td className="p-2 whitespace-nowrap">{row.workdat}</td>
                                            <td className="p-1"><input type="text" value={row.timeup || ''} onChange={e => handleInputChange(row.autoid, 'timeup', e.target.value)} onBlur={e => handleTimeBlur(row.autoid, 'timeup', e.target.value)} className="w-20 p-1 border rounded-md bg-white"/></td>
                                            <td className="p-1"><input type="text" value={row.timedown || ''} onChange={e => handleInputChange(row.autoid, 'timedown', e.target.value)} onBlur={e => handleTimeBlur(row.autoid, 'timedown', e.target.value)} className="w-20 p-1 border rounded-md bg-white"/></td>
                                            <td className="p-1"><input type="text" value={formatInputValue(row.Kzhour)} onChange={e => handleInputChange(row.autoid, 'Kzhour', e.target.value)} className="w-16 p-1 border rounded-md text-center"/></td>
                                            <td className="p-1"><input type="text" value={formatInputValue(row.TOTH)} onChange={e => handleInputChange(row.autoid, 'TOTH', e.target.value)} className="w-16 p-1 border rounded-md text-center"/></td>
                                            <td className="p-1"><input type="text" value={formatInputValue(row.H1)} onChange={e => handleInputChange(row.autoid, 'H1', e.target.value)} className="w-16 p-1 border rounded-md text-center"/></td>
                                            <td className="p-1"><input type="text" value={formatInputValue(row.H2)} onChange={e => handleInputChange(row.autoid, 'H2', e.target.value)} className="w-16 p-1 border rounded-md text-center"/></td>
                                            <td className="p-1"><input type="text" value={formatInputValue(row.H3)} onChange={e => handleInputChange(row.autoid, 'H3', e.target.value)} className="w-16 p-1 border rounded-md text-center"/></td>
                                            <td className="p-1"><input type="text" value={formatInputValue(row.B3)} onChange={e => handleInputChange(row.autoid, 'B3', e.target.value)} className="w-16 p-1 border rounded-md text-center"/></td>
                                            <td className="p-1"><input type="text" value={formatInputValue(row.B4)} onChange={e => handleInputChange(row.autoid, 'B4', e.target.value)} className="w-16 p-1 border rounded-md text-center"/></td>
                                            <td className="p-1"><input type="text" value={formatInputValue(row.B5)} onChange={e => handleInputChange(row.autoid, 'B5', e.target.value)} className="w-16 p-1 border rounded-md text-center"/></td>
                                            <td className="p-2 whitespace-nowrap text-gray-700 font-medium">{jiaTypeMap[jiaTypeCode] || row.memo || ''}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {Object.keys(editedRows).length > 0 && (
                        <div className="mt-6 flex justify-end gap-4">
                            <button onClick={handleCancelChanges} type="button" className="px-6 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
                                Hủy
                            </button>
                            <button onClick={handleSaveAll} disabled={isLoading} className="px-6 py-2 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 disabled:bg-gray-400">
                                {isLoading ? 'Đang lưu...' : `Lưu ${Object.keys(editedRows).length} thay đổi`}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}